'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gallery like state.
 *
 * Counts are shared and live in Postgres; which items *this* visitor liked is
 * device-local. There is no login, so localStorage is the only place that
 * distinction can live — it keeps the heart filled on return visits and stops a
 * second tap from double-counting.
 *
 * Counts are fetched here rather than server-rendered because /work is ISR with
 * a 3600s window: a count baked into the page would be up to an hour stale and
 * identical for everyone until regeneration.
 */

const STORAGE_KEY = 'nebras:gallery-likes';

function readStoredLikes(): Set<number> {
    if (typeof window === 'undefined') return new Set();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return new Set(Array.isArray(parsed) ? parsed.filter((v) => Number.isInteger(v)) : []);
    } catch {
        // Corrupt or unavailable storage (private mode, quota) must not break the
        // gallery — the visitor just starts with nothing liked.
        return new Set();
    }
}

function persistLikes(liked: Set<number>) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...liked]));
    } catch {
        // Ignore: the like still registered server-side, it just will not survive
        // a reload on this device.
    }
}

export function useGalleryLikes(itemIds: number[]) {
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [liked, setLiked] = useState<Set<number>>(new Set());
    const inFlight = useRef<Set<number>>(new Set());

    useEffect(() => {
        setLiked(readStoredLikes());
    }, []);

    const idsKey = itemIds.join(',');

    useEffect(() => {
        if (!idsKey) return;
        let cancelled = false;

        (async () => {
            try {
                const res = await fetch(`/api/gallery/like?ids=${idsKey}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && data?.likes) setCounts(data.likes);
            } catch {
                // Counts are decoration — a failed fetch leaves them at zero rather
                // than blocking the gallery.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [idsKey]);

    const toggleLike = useCallback(
        async (id: number, next?: boolean) => {
            // Guard against a rapid double-fire on the same item racing itself.
            if (inFlight.current.has(id)) return;

            const shouldLike = next ?? !liked.has(id);
            if (shouldLike === liked.has(id)) return;

            inFlight.current.add(id);

            // Optimistic: the heart and the number move on the same frame as the tap.
            setLiked((prev) => {
                const updated = new Set(prev);
                if (shouldLike) updated.add(id);
                else updated.delete(id);
                persistLikes(updated);
                return updated;
            });
            setCounts((prev) => ({
                ...prev,
                [id]: Math.max(0, (prev[id] ?? 0) + (shouldLike ? 1 : -1)),
            }));

            try {
                const res = await fetch('/api/gallery/like', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id, liked: shouldLike }),
                });

                if (!res.ok) throw new Error(String(res.status));

                // Trust the server's total — it accounts for everyone else's likes
                // since this page loaded.
                const data = await res.json();
                if (typeof data?.likes === 'number') {
                    setCounts((prev) => ({ ...prev, [id]: data.likes }));
                }
            } catch {
                // Roll the optimistic update back so the UI never claims a like the
                // server did not record.
                setLiked((prev) => {
                    const reverted = new Set(prev);
                    if (shouldLike) reverted.delete(id);
                    else reverted.add(id);
                    persistLikes(reverted);
                    return reverted;
                });
                setCounts((prev) => ({
                    ...prev,
                    [id]: Math.max(0, (prev[id] ?? 0) + (shouldLike ? -1 : 1)),
                }));
            } finally {
                inFlight.current.delete(id);
            }
        },
        [liked]
    );

    return { counts, liked, toggleLike };
}
